import React, { FunctionComponent, useCallback, useMemo, useState } from "react";
import { DateTime } from "luxon";

import { MetricDataInterval, MetricDataIntervalTitleMap } from "@tether-web-portal/types/metricsTypes";
import { TimestampRange } from "@tether-web-portal/types/TimestampRange";

import { useTimezone } from "../hooks/useTimezone";
import { useStateUpdate } from "@tether-web-portal/hooks";

const intervalToDateRangeData = (interval: MetricDataInterval, zone: string) => {
  const now = DateTime.now().setZone(zone);
  let toTimestamp = now.plus({ hours: 1 }).startOf("hour");
  let fromTimestamp = toTimestamp;

  switch (interval) {
    case MetricDataInterval.MINUTES_15:
      toTimestamp = now.plus({ minutes: 1 }).startOf("minute");
      fromTimestamp = toTimestamp.minus({ minutes: 15 });
      break;
    case MetricDataInterval.HOURS_24:
      fromTimestamp = toTimestamp.minus({ days: 1 });
      break;
    case MetricDataInterval.DAYS_7:
      fromTimestamp = toTimestamp.minus({ days: 7 });
      break;
    case MetricDataInterval.DAYS_30:
      fromTimestamp = toTimestamp.minus({ days: 30 });
      break;
    case MetricDataInterval.DAYS_90:
      fromTimestamp = toTimestamp.minus({ days: 90 });
      break;
    case MetricDataInterval.MONTHS_3:
      fromTimestamp = toTimestamp.minus({ months: 3 });
      break;
    case MetricDataInterval.CURRENT_MONTH:
      fromTimestamp = toTimestamp.startOf("month");
      break;
    case MetricDataInterval.LAST_MONTH:
      toTimestamp = toTimestamp.minus({ months: 1 }).endOf("month");
      fromTimestamp = toTimestamp.startOf("month");
      break;
    case MetricDataInterval.CURRENT_YEAR:
      fromTimestamp = toTimestamp.startOf("year");
      break;
    case MetricDataInterval.LAST_YEAR:
      toTimestamp = now.minus({ years: 1 }).endOf("year");
      fromTimestamp = toTimestamp.startOf("year");
      break;
    default: {
      // Check if any MetricDataInterval are not covered by the switch case
      const exhaustiveCheck: MetricDataInterval.CUSTOM = interval;
      break;
    }
  }


  const fromUtc = fromTimestamp.toUTC();
  const toUtc = toTimestamp.toUTC();
  const metricsNumberOfDaysToShow = toUtc.diff(fromUtc).as("days");

  return {
    metricsNumberOfDaysToShow,
    metricsFromTimestamp: fromUtc.toISO() ?? "",
    metricsToTimestamp: toUtc.toISO() ?? "",
    metricsCurrentDataInterval: interval,
    metricsCurrentDataIntervalLabel: MetricDataIntervalTitleMap[interval],
  };
};

interface IMetricsIntervalContext {
  initialized: boolean;
  setInitialized: () => void;
  metricsNumberOfDaysToShow: number;
  previousMetricsTimeStamps: TimestampRange;
  metricsTimeStamps: TimestampRange;
  metricsCurrentDataInterval: MetricDataInterval;
  metricsCurrentDataIntervalLabel: string;
  setMetricsInterval: (interval: MetricDataInterval) => void;
  setCustomMetricsInterval: (fromTimestamp: string, toTimestamp: string) => void;
}
interface IMetricsIntervalInternalState {
  initialized: boolean;
  metricsNumberOfDaysToShow: number;
  metricsFromTimestamp: string;
  metricsToTimestamp: string;
  metricsCurrentDataInterval: MetricDataInterval;
  metricsCurrentDataIntervalLabel: string;
}

export const MetricsIntervalContext = React.createContext<IMetricsIntervalContext>({
  initialized: false,
  setInitialized: () => null,
  metricsNumberOfDaysToShow: 1,
  metricsTimeStamps: {
    fromTimestamp: DateTime.utc().minus({ days: 1 }).startOf("hour").toISO() ?? "",
    toTimestamp: DateTime.utc().plus({ hours: 1 }).startOf("hour").toISO() ?? "",
  },
  previousMetricsTimeStamps: {
    fromTimestamp: DateTime.utc().minus({ days: 2 }).startOf("hour").toISO() ?? "",
    toTimestamp: DateTime.utc().plus({ hours: 1 }).minus({ days: 1 }).endOf("hour").toISO() ?? "",
  },
  metricsCurrentDataInterval: MetricDataInterval.HOURS_24,
  metricsCurrentDataIntervalLabel: MetricDataIntervalTitleMap[MetricDataInterval.HOURS_24],
  setMetricsInterval: () => null,
  setCustomMetricsInterval: () => null,
});

interface IMetricsIntervalContextProps {
  defaultInterval?: MetricDataInterval;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
}
export const MetricsIntervalContextProvider: FunctionComponent<IMetricsIntervalContextProps> = ({
  defaultInterval,
  children,
}: IMetricsIntervalContextProps) => {
  const timezone = useTimezone();

  const [contextState, setContextState] = useState<IMetricsIntervalInternalState>(() => ({
    initialized: false,
    ...intervalToDateRangeData(defaultInterval || MetricDataInterval.HOURS_24, timezone),
  }));

  useStateUpdate(
    () => {
      setContextState((state) => ({
        ...state,
        ...intervalToDateRangeData(defaultInterval || MetricDataInterval.HOURS_24, timezone),
      }));
    },
    [timezone],
  )

  const setInitialized = useCallback(() => {
    if (!contextState?.initialized) {
      setContextState((state) => ({
        ...state,
        initialized: true,
      }));
    }
  }, [contextState?.initialized]);

  const setMetricsInterval = useCallback(
    (interval: MetricDataInterval) => {
      setContextState((state) => ({
        ...state,
        ...intervalToDateRangeData(interval, timezone),
      }));
    },
    [timezone],
  );

  const setCustomMetricsInterval = useCallback(
    (fromTimestamp: string, toTimestamp: string) => {
      const fromUtc = DateTime.fromISO(fromTimestamp, { setZone: true }).toUTC();
      const toUtc = DateTime.fromISO(toTimestamp, { setZone: true }).toUTC();
      if (!fromUtc.isValid || !toUtc.isValid) return;

      const daysBetween = Math.floor(toUtc.diff(fromUtc).as("days"));
      const fromZonedLabel = fromUtc.setZone(timezone).toLocaleString(DateTime.DATE_SHORT);
      const toZonedLabel = toUtc.setZone(timezone).toLocaleString(DateTime.DATE_SHORT);
      const label = `${fromZonedLabel}-${toZonedLabel}`;

      setContextState((state) => ({
        ...state,
        metricsNumberOfDaysToShow: daysBetween,
        metricsFromTimestamp: fromUtc.toISO() ?? fromTimestamp,
        metricsToTimestamp: toUtc.toISO() ?? toTimestamp,
        metricsCurrentDataInterval: MetricDataInterval.CUSTOM,
        metricsCurrentDataIntervalLabel: label,
      }));
    },
    [timezone],
  );

  const { metricsFromTimestamp, metricsToTimestamp } = contextState;

  const previousMetricsTimeStamps = useMemo<TimestampRange>(() => {
    const fromUtc = DateTime.fromISO(metricsFromTimestamp, { zone: "utc" });
    const toUtc = DateTime.fromISO(metricsToTimestamp, { zone: "utc" });
    const minutes = toUtc.diff(fromUtc).as("minutes");
    return {
      fromTimestamp: fromUtc.minus({ minutes }).toUTC().toISO() ?? "",
      toTimestamp: metricsFromTimestamp,
    };
  }, [metricsFromTimestamp, metricsToTimestamp]);

  const metricsTimeStamps = useMemo(
    () => ({
      fromTimestamp: metricsFromTimestamp,
      toTimestamp: metricsToTimestamp,
    }),
    [metricsFromTimestamp, metricsToTimestamp],
  );


  return (
    <MetricsIntervalContext.Provider
      value={{
        ...contextState,
        metricsTimeStamps,
        previousMetricsTimeStamps,
        setMetricsInterval,
        setCustomMetricsInterval,
        setInitialized,
      }}
    >
      {children}
    </MetricsIntervalContext.Provider>
  );
};
